/**
 * The bench itself — the owner's ask, verbatim: "Workbench needs a real workbench graphic and
 * animations when things are being crafted."
 *
 * So this is a room, not a form. One hand-drawn SVG in the Requiem language (angular slab, splayed
 * legs, a vice, hull-metal offcuts, and a lamp that is the only lit thing in the picture), plus
 * four things that move while the bench is working:
 *
 *   - the lamp flame breathes,
 *   - sparks fly off the vice (a fixed pool of spans, animated in CSS — no per-frame JS),
 *   - the part on the bench assembles in five stages keyed to the *server's* progress,
 *   - a Bebas countdown ticks down to the server's `readyAt`.
 *
 * When a job lands, the whole scene takes a mint flash and a READY sash and plays `ui_confirm`.
 *
 * Everything authoritative is the server's: `startedAt` and `readyAt` are unix seconds on the
 * server's clock, and the component only ever uses the browser's clock through `skewSec`, the
 * offset measured at load. A tab that has been asleep for an hour therefore wakes up with the
 * right answer rather than a job that is eternally "0:03 to go".
 *
 * Standalone by construction: it mounts at the top of `Workbench.tsx` but owns its own fetch, so
 * neither file needs to know anything about the other.
 */

import type { VNode } from 'preact';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import {
  CRAFT_QUEUE_MAX,
  ITEMS,
  RECIPES,
  craftProgress,
  craftStage,
  recipeDurationSec,
  type Recipe,
} from '@stillsun/shared';
import {
  cancelCraftJob,
  collectCraftJob,
  fetchCraftJobs,
  onBenchChange,
  startCraftJob,
  type CraftJob,
  type CraftJobsResponse,
} from '../../net/economy-api.js';
import { session } from '../../net/session.js';
import { sound } from '../../audio/sound.js';
import { Icon } from '../icons.js';
import { Cap, LEDGER_SILENT, Loading, errText, rarityRailHex, useLoader, wick } from './common.js';
import './workbench.css';

export interface WorkbenchSceneProps {
  nonce: number;
  token: string;
}

/** How many spark spans the vice throws. Fixed pool: created once, animated entirely in CSS. */
const SPARKS = 10;

const nameOf = (itemId: string): string => ITEMS[itemId]?.name ?? itemId;

/** m:ss, Bebas-friendly. Anything over an hour shows h:mm:ss rather than a four-digit minute. */
export function countdown(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const mm = Math.floor(s / 60);
  const ss = s % 60;
  if (mm < 60) return `${String(mm)}:${String(ss).padStart(2, '0')}`;
  return `${String(Math.floor(mm / 60))}:${String(mm % 60).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
}

/** The job the bench is actually working: the one finishing soonest that has not finished yet. */
function activeJob(jobs: readonly CraftJob[], nowSec: number): CraftJob | null {
  let best: CraftJob | null = null;
  for (const job of jobs) {
    if (job.readyAt <= nowSec) continue;
    if (best === null || job.readyAt < best.readyAt) best = job;
  }
  return best;
}

export function WorkbenchScene(props: WorkbenchSceneProps): VNode {
  const { token } = props;
  const bench = useLoader<CraftJobsResponse>(
    () => fetchCraftJobs(token),
    `craftjobs:${String(props.nonce)}:${token}`,
  );
  const [jobs, setJobs] = useState<CraftJob[] | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState(false);
  const [picking, setPicking] = useState(false);
  /** serverNow - browserNow, in seconds. Measured once per load; the bench clock rides on it. */
  const [skewSec, setSkewSec] = useState(0);
  const [tick, setTick] = useState(0);
  const wasReady = useRef(new Set<number>());

  // The loader answers with the server's own `now`; everything after this is drawn from the
  // browser's clock corrected by that one measurement.
  useEffect(() => {
    if (bench.data === null) return;
    setJobs(bench.data.jobs);
    setSkewSec(bench.data.nowSec - Math.floor(Date.now() / 1000));
  }, [bench.data]);

  // The recipe list below the bench enqueues through the same seam; this is how the drawing hears
  // about a job it did not start itself.
  useEffect(
    () =>
      onBenchChange((next) => {
        setJobs(next);
      }),
    [],
  );

  const list = jobs ?? bench.data?.jobs ?? [];
  const nowSec = Math.floor(Date.now() / 1000) + skewSec;

  // One second is the right cadence for a countdown and cheap enough to leave running while the
  // tab is open; it stops entirely once nothing is working.
  const working = list.some((j) => j.readyAt > nowSec);
  useEffect(() => {
    if (!working) return;
    const timer = window.setInterval(() => {
      setTick((t) => t + 1);
    }, 1000);
    return () => {
      window.clearInterval(timer);
    };
  }, [working, list.length]);
  void tick;

  // The flourish: the first time a job crosses its `readyAt` while the tab is watching, the whole
  // scene flashes mint and the bench says so out loud.
  useEffect(() => {
    let landed = false;
    for (const job of list) {
      if (job.readyAt > nowSec) continue;
      if (wasReady.current.has(job.id)) continue;
      wasReady.current.add(job.id);
      landed = true;
    }
    if (!landed) return;
    setFlash(true);
    try {
      sound.play('ui_confirm');
    } catch {
      /* audio not unlocked yet: the picture still says it */
    }
    const timer = window.setTimeout(() => {
      setFlash(false);
    }, 1400);
    return () => {
      window.clearTimeout(timer);
    };
  }, [list, nowSec]);

  const active = activeJob(list, nowSec);
  const readyCount = list.filter((j) => j.readyAt <= nowSec).length;
  const progress =
    active === null
      ? readyCount > 0
        ? 1
        : 0
      : craftProgress(active.startedAt, active.readyAt, nowSec);
  const stage = craftStage(progress);
  const state = active !== null ? 'working' : readyCount > 0 ? 'ready' : 'idle';

  const act = (run: () => Promise<CraftJob[]>, ok: string): void => {
    if (busy) return;
    setBusy(true);
    setNote('');
    void (async () => {
      try {
        setJobs(await run());
        setNote(ok);
        // Wick moved, and so may the stash; the header and the recipe list below are both stale.
        await session.refresh();
        bench.reload();
      } catch (err) {
        setNote(errText(err, 'The bench refused that one.'));
      } finally {
        setBusy(false);
      }
    })();
  };

  if (bench.loading && bench.data === null) {
    return (
      <section class="wb">
        <Loading />
      </section>
    );
  }

  const level = bench.data?.workbenchLevel ?? 1;
  const materials = bench.data?.materials ?? {};
  const purse = bench.data?.wick ?? session.player?.wick ?? 0;
  const queueMax = bench.data?.queueMax ?? CRAFT_QUEUE_MAX;
  const full = list.length >= queueMax;

  /** What this bench could start right now: level, materials and Wick all satisfied. */
  const ready: Recipe[] = (Object.values(RECIPES) as Recipe[])
    .filter(
      (r) =>
        r.level <= level &&
        r.fee <= purse &&
        r.inputs.every((i) => (materials[i.itemId] ?? 0) >= i.qty),
    )
    .sort((a, b) => a.fee - b.fee || a.name.localeCompare(b.name));

  return (
    <section class={`wb${flash ? ' flash' : ''}`} data-state={state}>
      <header class="wb-head">
        <Cap icon="workbench">The Bench</Cap>
        <span class="wb-head-meta">
          {`Module ${String(level)} · ${String(list.length)}/${String(queueMax)} on the bench`}
        </span>
      </header>

      {/* Two columns on anything wider than a phone: the room on the left, the paperwork on the
          right. One column below that, drawing first. */}
      <div class="wb-body">
        <div class="wb-stage" data-stage={String(stage)} aria-hidden="true">
          <BenchArt />
          <div class="wb-sparks">
            {Array.from({ length: SPARKS }, (_, i) => (
              <span
                key={i}
                class="wb-spark"
                style={{ '--i': String(i) } as Record<string, string>}
              />
            ))}
          </div>
          <div class="wb-progress-rail">
            <div
              class="wb-progress-fill"
              style={{ width: `${String(Math.round(progress * 100))}%` }}
            />
          </div>
          <div class="wb-sash">Ready</div>
        </div>

        <div class="wb-side">
          <div class="wb-readout">
            {active !== null ? (
              <>
                <span class="wb-clock">{countdown(active.readyAt - nowSec)}</span>
                <span class="wb-what">{nameOf(active.output.itemId)}</span>
              </>
            ) : readyCount > 0 ? (
              <>
                <span class="wb-clock done">Done</span>
                <span class="wb-what">
                  {readyCount > 1
                    ? `${String(readyCount)} pieces off the bench`
                    : 'A piece off the bench'}
                </span>
              </>
            ) : (
              <>
                <span class="wb-clock idle">Cold</span>
                <span class="wb-what">The lamp is lit. Nothing is on the bench.</span>
              </>
            )}
          </div>

          {note.length > 0 ? <p class="wb-note">{note}</p> : null}
          {bench.data === null ? <p class="wb-note">{LEDGER_SILENT}</p> : null}

          {list.length > 0 ? (
            <ul class="wb-jobs">
              {list.map((job) => {
                const done = job.readyAt <= nowSec;
                const pct = Math.round(craftProgress(job.startedAt, job.readyAt, nowSec) * 100);
                const outDef = ITEMS[job.output.itemId];
                return (
                  <li key={String(job.id)} class={`wb-job${done ? ' done' : ''}`}>
                    <span
                      class="wb-job-fill"
                      style={{ width: `${String(pct)}%` }}
                      aria-hidden="true"
                    />
                    <Icon name={done ? 'benchReady' : 'workbench'} size={16} class="wb-job-ic" />
                    <span class="wb-job-name">
                      {nameOf(job.output.itemId)}
                      {job.output.qty > 1 ? (
                        <span class="wb-job-qty">{` ×${String(job.output.qty)}`}</span>
                      ) : null}
                    </span>
                    <span class="wb-job-time">
                      {done ? 'Ready' : countdown(job.readyAt - nowSec)}
                    </span>
                    <span class="wb-job-acts">
                      {done ? (
                        <button
                          type="button"
                          class="wb-btn wb-btn-go"
                          disabled={busy}
                          onClick={() => {
                            act(
                              () => collectCraftJob(token, job.id),
                              `${nameOf(job.output.itemId)} — in the stash.`,
                            );
                          }}
                        >
                          Collect
                        </button>
                      ) : (
                        <button
                          type="button"
                          class="wb-btn wb-btn-quiet"
                          disabled={busy}
                          onClick={() => {
                            act(
                              () => cancelCraftJob(token, job.id),
                              'Pulled off the bench. Materials back, half the Wick kept.',
                            );
                          }}
                        >
                          Pull
                        </button>
                      )}
                    </span>
                    {/* the rarity rail: the one place the ladder's colour shows on a job card */}
                    <span
                      class="wb-job-rail"
                      aria-hidden="true"
                      style={{ background: outDef ? rarityRailHex(outDef.rarity) : 'var(--rule)' }}
                    />
                  </li>
                );
              })}
            </ul>
          ) : null}

          <div class="wb-start">
            <button
              type="button"
              class="wb-btn wb-btn-open"
              disabled={busy || full || bench.data === null}
              onClick={() => {
                setPicking((p) => !p);
              }}
            >
              {full
                ? `Bench full — ${String(queueMax)} jobs`
                : picking
                  ? 'Close'
                  : 'Put something on the bench'}
            </button>
            {picking && !full ? (
              ready.length === 0 ? (
                <p class="wb-note">
                  Nothing you hold can be worked right now. Bring back materials, or sell for Wick.
                </p>
              ) : (
                <div class="wb-picks">
                  {ready.map((r) => (
                    <button
                      key={r.id}
                      type="button"
                      class="wb-pick"
                      disabled={busy}
                      onClick={() => {
                        setPicking(false);
                        act(() => startCraftJob(token, r.id), `${r.name} — the bench has it.`);
                      }}
                    >
                      <span class="wb-pick-name">{r.name}</span>
                      <span class="wb-pick-meta">
                        {`${wick(r.fee)} Wick · ${countdown(recipeDurationSec(r.id))}`}
                      </span>
                    </button>
                  ))}
                </div>
              )
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}

/**
 * The drawing. Static geometry only — every moving part is a class the CSS animates, so this
 * function renders once and is never re-run by a tick.
 *
 * The room, left to right: a vice bolted to the slab (where the sparks come off), the jig the
 * part assembles in, an offcut pile, and the lamp. Nothing is round; nothing glows but the flame.
 */
function BenchArt(): VNode {
  return (
    <svg class="wb-art" viewBox="0 0 320 180" role="img" aria-label="The workbench">
      {/* the wall behind: two hull seams, so the bench is in a room and not in space */}
      <g class="wb-wall">
        <path d="M0 34h320M0 62h320" />
        <path d="M64 34v28M198 34v28M282 34v28" />
      </g>

      {/* the slab and its legs */}
      <g class="wb-frame">
        <path d="M28 108h264l-14 14H42z" />
        <path d="M50 122 40 168M86 122l6 46M234 122l-6 46M270 122l10 46" />
        <path d="M46 146h48M232 146h48" />
        <path d="M0 168h320" />
      </g>

      {/* the vice, bolted to the near-left corner — this is where the sparks come off */}
      <g class="wb-vice">
        <path d="M44 84h40v24H44z" />
        <path d="M52 84V72h24v12" />
        <path d="M62 72V60h4v12" />
        <path d="M84 96h14" />
        <path d="M98 92v8" />
      </g>

      {/* the jig: two brackets the part is built between. Always drawn; the part is not. */}
      <g class="wb-jig">
        <path d="M126 108V88h10M204 108V88h-10" />
        <path d="M126 98h10M194 98h10" />
      </g>

      {/* the part, in four stages. CSS reveals these by the scene's data-stage. */}
      <g class="wb-part" data-p="1">
        <path d="M136 100h58v8h-58z" />
      </g>
      <g class="wb-part" data-p="2">
        <path d="M142 88h46v12h-46z" />
        <path d="M152 88v12M178 88v12" />
      </g>
      <g class="wb-part" data-p="3">
        <path d="M158 74h14v14h-14z" />
        <path d="M158 81h14" />
      </g>
      <g class="wb-part" data-p="4">
        <path d="M136 68h14l6-8h18l6 8h14" />
        <path d="M144 68v-6M186 68v-6" />
      </g>

      {/* offcuts: hull metal, cut square, stacked because nobody tidies */}
      <g class="wb-scrap">
        <path d="M212 100h26v8h-26z" />
        <path d="M218 92h16v8h-16z" />
        <path d="M244 104h18v4h-18z" />
      </g>

      {/* the lamp — post, hood, and the one lit thing in the picture */}
      <g class="wb-lamp">
        <path d="M276 108V40" />
        <path d="M258 40h36l-8 12h-20z" />
        <path d="M268 108h16" />
      </g>
      <g class="wb-flame">
        <path d="M276 74l7 9-7 10-7-10z" />
        <path d="M276 80l3 4-3 5-3-5z" class="wb-flame-core" />
      </g>
    </svg>
  );
}
